import CustomerRecord from '@core/classes/Customer/CustomerRecord';
import { generateMinimumParametersForPlaidPage } from '@core/helpers/Providers/plaid-helper';
import LogRocket from 'logrocket';
import { PLAID_VERIFICATION_STATUS } from '@core/classes/Enums';
import util from '@core/services/util';

/**
 * Checks if the store has IDV enabled (the select box on store settings)
 * @param {object} store The store in question
 * @returns {boolean} Whether or not the IDV check is enabled.
 */
export function isIDVEnabled(store: any): boolean {
	return store?.storeSettings?.isCustomerPlaidIDVEnabled ?? false;
}

/**
 * @param {CustomerRecord} customer
 * @returns Boolean, Either complete or incomplete, for Pill label on Pill.vue for Customer portal
 */
export function getIDVPillLabel(customer: CustomerRecord): string {
	const status: 'Complete' | 'Incomplete' | ' ' | 'Failed' = getIDVStatus(customer);
	const englishCompleted = 'Completed';
	const englishIncomplete = 'Incomplete';
	const englishRejected = 'Pending';
	const spanishCompleted = 'Completado';
	const spanishIncomplete = 'Incompletado';
	const spanishRejected = 'Pendiente';

	const incompleteText = customer.language == 'English' ? englishIncomplete : spanishIncomplete;
	const rejectedText = customer.language == 'English' ? englishRejected : spanishRejected;

	const completedText = customer.language == 'English' ? englishCompleted : spanishCompleted;

	if (status == 'Incomplete') return incompleteText;
	if (status == 'Complete') return completedText;
	if (status == 'Failed') return rejectedText;
}

/**
 * @param {CustomerRecord} customer
 * @returns Error or Success, for the Pill status color on Pill.vue for Customer portal
 */
export function getIDVPillStatusColor(customer: CustomerRecord): string {
	const status: 'Complete' | 'Incomplete' | ' ' | 'Failed' = getIDVStatus(customer);
	if (status === 'Incomplete') return 'error';
	if (status === 'Failed') return 'warning';
	if (status === 'Complete') return 'success';
}

/**
 * @param  {CustomerRecord} customer
 * @returns Helper function that returns complete, incomplete, or empty string (to not show) for the Pill.vue in Customer portal
 */
function getIDVStatus(customer: CustomerRecord): 'Complete' | 'Incomplete' | ' ' | 'Failed' {
	if (!customer?.id) return ' ';

	// If customer skipped IDV, use that status instead so we can override the "Failed" status when F&I have passed the session
	const customerSkippedIDV: boolean = customer.idvOverride && customer.idvOverride.length > 0 && customer.isIDVSkipped;
	const customerPassedIDV: boolean = customer.statusIDV == PLAID_VERIFICATION_STATUS.Success.ind;
	if (customerPassedIDV || customerSkippedIDV) return 'Complete';

	const failedStatus: boolean = customer.statusIDV == PLAID_VERIFICATION_STATUS.Failed.ind;
	if (failedStatus) return 'Failed';

	const neverStartedIDV: boolean = customer.statusIDV == null;
	const incompleteStatus: boolean = customer.statusIDV !== PLAID_VERIFICATION_STATUS.Success.ind;
	if (neverStartedIDV || incompleteStatus) return 'Incomplete';
}

/**
 * @param {CustomerRecord} customer
 * @returns Boolean indicating if the IDV button should be disabled
 * Rules:
 * - Enable if statusIDV is null (never started)
 * - Enable if statusIDV is Active
 * - Disable in all other cases
 */
export function disableIDVButton(customer: CustomerRecord): boolean {
	if (!customer?.id) return true;
	const idvHasNotStarted = customer.statusIDV == null;
	const isActive = customer.statusIDV == PLAID_VERIFICATION_STATUS.Active.ind;

	// Enable if never started or if active
	return !(idvHasNotStarted || isActive);
}

// export async function idvAction(customer: CustomerRecord) {
//   await PlaidHelper.generatePlaidPortalLink(customer);
//}

export async function idvAction(router: any, id: string, customer: CustomerRecord) {
	let linkToken: string | undefined;
	let associations: any[] | undefined;
	try {
		const response = await generateMinimumParametersForPlaidPage(customer, customer.storeCodes[0]);
		linkToken = response.linkToken;
		associations = response.associations;
	} catch (error) {
		util.toastr('error', 'Error', 'Error initiating identity verification.');
		LogRocket.error(error);
	}

	if (linkToken && associations) {
		router.push({
			name: 'identity-verification',
			query: {
				id: id,
				linkToken: linkToken,
				customerAssociations: JSON.stringify(associations),
			},
		});
	}
}
const IDVHelper = {
	idvAction,
	isIDVEnabled,
	getIDVStatus,
	getIDVPillLabel,
	disableIDVButton,
	getIDVPillStatusColor,
};

export default IDVHelper;
